import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import modalI18n from 'view/search-modal/search-bar/messages';
import * as elevation from 'view/styles/elevation';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import { searchTextbox, searchTextboxBackground } from 'view/styles/z-index';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { Flipped } from '@atlassian/help-center-common-component/flip-toolkit';
import { UnstyledButton } from '@atlassian/help-center-common-component/unstyled-button';
import i18n from './messages';

interface Props {
    value: string;
    onChange: AnyFunction1<React.ChangeEvent<HTMLInputElement>>;
    onSearch: AnyFunction;
    closeCustomizeSidebar: AnyFunction;
    onFocus?: () => void;
    autoFocus?: boolean;
    id?: string;
    searchBarShown?: boolean;
    placeHolderText?: string;
}

export class SearchTextbox extends React.Component<Props & InjectedIntlProps> {
    onSearch = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLFormElement>) => {
        const { onSearch, closeCustomizeSidebar } = this.props;
        e.preventDefault();
        onSearch();
        closeCustomizeSidebar();
    };

    render() {
        const { intl, value, onChange, onFocus, autoFocus, searchBarShown, placeHolderText, id } = this.props;
        const searchBarId = id || '';
        const inputElement = (
            // eslint-disable-next-line styled-components-a11y/no-autofocus
            <Input
                type="text"
                name="term"
                autoComplete="off"
                spellCheck={false}
                placeholder={placeHolderText || intl.formatMessage(modalI18n.input)}
                value={value}
                onFocus={onFocus}
                onChange={onChange}
                // Disabling existing violations, should be fixed when revisited.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
            />
        );

        const showSearchTextbox = !searchBarShown;

        const animatedInputAndBackground = (
            <React.Fragment>
                {showSearchTextbox ? (
                    <Flipped flipId={`${searchBarId}search`} translate>
                        {inputElement}
                    </Flipped>
                ) : (
                    inputElement
                )}
                {/* The background must come after the input element in the DOM, so that the css selector for applying box shadowing will work properly */}
                {showSearchTextbox && (
                    <Flipped flipId={`${searchBarId}background`} translate scale>
                        <InputBackground />
                    </Flipped>
                )}
            </React.Fragment>
        );
        return (
            // eslint-disable-next-line styled-components-a11y/no-noninteractive-element-interactions
            <Form
                onSubmit={this.onSearch}
                shown={showSearchTextbox}
                // search is opened on mouse down instead of click to avoid situations
                // in which users get focus without the search bar expanding
                onMouseDown={this.onSearch}
                id={searchBarId}
            >
                {animatedInputAndBackground}
                {showSearchTextbox && (
                    <Flipped flipId={`${searchBarId}icon`} translate>
                        <IconButton type="submit" actionSubjectId="search">
                            <SearchIcon
                                primaryColor={token('color.icon', colors.N200)}
                                label={intl.formatMessage(i18n.searchButton)}
                            />
                        </IconButton>
                    </Flipped>
                )}
            </Form>
        );
    }
}

export default injectIntl(SearchTextbox);

const borderRadius = css`
    border-radius: 5px;
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${borderRadius};
    ${mixins.placeholder(token('color.text.subtlest', colors.N200))};
    padding: ${grid.multiple(2.125).px};
    padding-right: ${token('space.400', '32px')};
    width: 100%;
    border: 0;
    outline: none;
    position: relative;
    z-index: ${searchTextbox};
`;

const InputBackground = styled.div`
    ${borderRadius};
    background-color: ${token('color.background.input', colors.N0)};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: box-shadow ${transitions.speedMs.slow}ms ${transitions.timingFunctions.easeInOut};
    z-index: ${searchTextboxBackground};

    /* box shadow should apply to the background element so it animates properly,
       but should only apply on hover or focus on the sibling input element */
    ${/* sc-selector */ Input}:hover + &,
    ${/* sc-selector */ Input}:focus + & {
        ${elevation.e300};
        z-index: ${searchTextboxBackground};
    }
`;

interface FormProps {
    shown: boolean;
}

// eslint-disable-next-line rulesdir/no-styled-export
export const Form = styled.form<FormProps>`
    position: relative;
    opacity: ${(props) => (props.shown ? '1' : '0')};
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const IconButton = styled(UnstyledButton)`
    position: absolute;
    padding: 0 ${token('space.200', '16px')};
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    border-radius: 0 ${mixins.borderRadiusValue} ${mixins.borderRadiusValue} 0;
    cursor: pointer;
    z-index: ${searchTextbox};

    &:hover {
        > span {
            ${transitions.zoom()};
        }
    }
`;
